import { Button, Title, Tooltip } from "rizzui";
import { IEnquiryEntry } from "@/utilities/types/EnquiryEntry";
import { useMemo, useState } from "react";
import AgentDetailsModal from "../modals/AgentDetailsModal";
import SpamDetailsModal from "../modals/SpamDetailsModal";
import { MdOutlineInfo } from "react-icons/md";

interface LeadInsightsProps {
  data?: IEnquiryEntry;
}

export default function LeadInsights({ data }: LeadInsightsProps) {
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [isSpamModalOpen, setIsSpamModalOpen] = useState(false);

  const recentlyContacted = useMemo(() => {
    if (data?.recentlyContacted) {
      return `${data.recentlyContacted.uniqueUsers} ${data.recentlyContacted.uniqueUsers === 1 ? "agent" : "agents"} in ${data.recentlyContacted.days} ${data.recentlyContacted.days === 1 ? "day" : "days"}`;
    }
    return "N/A";
  }, [data?.recentlyContacted]);

  // Device information formatting
  const deviceInfo = useMemo(() => {
    if (data?.device?.model && data?.device?.vendor) {
      return `${data.device.model} | ${data.device.vendor}`;
    }
    return "N/A";
  }, [data?.device]);

  return (
    <div className="space-y-4 md:space-y-6 p-3 bg-white">
      {/* Header */}
      <div>
        <div className="flex items-center gap-1">
          <Title as="h3" className="text-xl font-semibold text-gray-900">
            Lead Insights
          </Title>
          <Tooltip
            content={
              <div className="w-64 text-start">
                <p className="text-sm">
                  Summary of lead activity, contact history, fraud check, and
                  tracking info like location and device.
                </p>
              </div>
            }
          >
            <Button variant="text" className="p-0">
              <MdOutlineInfo size={20} />
            </Button>
          </Tooltip>
        </div>
        <p className="text-sm text-gray-500">
          These are interesting information about the enquirer.
        </p>
      </div>
      {/* Activity and FraudDetect Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Activity Section */}
        <div className="md:space-y-2">
          <Title as="h4" className="text-lg font-semibold text-gray-900">
            Activity
          </Title>

          <div className="space-y-2 md:space-y-4">
            <div>
              <div className="flex items-center gap-1">
                <div className="text-sm font-semibold text-gray-600">
                  Times Enquired for this lead
                </div>
                <Tooltip
                  content={
                    <div className="text-sm">
                      Total number of enquiries for this project.
                    </div>
                  }
                >
                  <Button variant="text" className="p-0 max-h-fit">
                    <MdOutlineInfo size={16} className="text-gray-700" />
                  </Button>
                </Tooltip>
              </div>
              <div className="text-gray-600">
                {data?.timesEnquired || "N/A"}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1">
                <div className="text-sm font-semibold text-gray-600">
                  Recently Contacted
                </div>
                <Tooltip
                  content={
                    <div className="max-w-52 text-sm text-left">
                      Shows how many unique agents have contacted this lead
                      within a specific number of days.
                    </div>
                  }
                >
                  <Button variant="text" className="p-0 max-h-fit">
                    <MdOutlineInfo size={16} className="text-gray-600" />
                  </Button>
                </Tooltip>
              </div>
              <div className="text-gray-600">{recentlyContacted}</div>
            </div>
          </div>
        </div>

        {/* FraudDetect Section */}
        <div className="md:space-y-2">
          <Title as="h4" className="text-lg font-semibold text-gray-900">
            FraudDetect™
          </Title>
          <div className="space-y-2 md:space-y-4">
            <div>
              <div className="text-sm font-semibold text-gray-600">
                Detected Property Agent?
              </div>
              <div className="text-gray-500">
                {`${data?.ceaInfo && data.isEcbAgent ? "Yes (ECB)" : data?.ceaInfo ? "Yes" : "No"}`}
                {data?.isEcbAgent && (
                  <>
                    <span> • </span>
                    <span
                      className="underline cursor-pointer"
                      onClick={() => setIsAgentModalOpen(true)}
                    >
                      View Details
                    </span>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-600">
                Detected Spam Enquiry?
              </div>
              <div className="text-gray-500">
                {`${data?.contactSaleSubmissionReport ? "Yes" : "No"}`}
                {data?.contactSaleSubmissionReport && (
                  <>
                    <span> • </span>
                    <span
                      className="underline cursor-pointer"
                      onClick={() => setIsSpamModalOpen(true)}
                    >
                      View Details
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tracking Section */}
      <div className="md:space-y-2">
        <Title as="h4" className="text-lg font-semibold text-gray-900">
          Tracking
        </Title>

        <div className="grid grid-cols-2 gap-4 md:gap-6">
          <div className="space-y-2">
            <div>
              <div className="text-sm text-gray-600 font-semibold">Country</div>
              <div className="text-gray-500">Singapore</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">
                IP Address
              </div>
              <div className="text-gray-500">{data?.ip || "N/A"}</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">GCLID</div>
              <div className="text-gray-500 w-full break-all">
                {data?.gclid || "N/A"}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">GBRAID</div>
              <div className="text-gray-500 w-full break-all">
                {data?.gbraid || "N/A"}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">
                CAMPAIGNID
              </div>
              <div className="text-gray-500 w-full break-all">
                {data?.campaignId || "N/A"}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div>
              <div className="text-sm text-gray-600 font-semibold">
                Browser Type
              </div>
              <div className="text-gray-500">{data?.browser || "N/A"}</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">
                Device Type
              </div>
              <div className="text-gray-500">{deviceInfo}</div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold ">
                Keyword
              </div>
              <div className="text-gray-500 w-full break-all">
                {data?.searchTerm || "N/A"}
              </div>
            </div>

            <div>
              <div className="text-sm text-gray-600 font-semibold">WBRAID</div>
              <div className="text-gray-500 w-full break-all">
                {data?.wbraid || "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AgentDetailsModal
        isOpen={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        ceaInfo={data?.ceaInfo}
      />

      <SpamDetailsModal
        isOpen={isSpamModalOpen}
        onClose={() => setIsSpamModalOpen(false)}
        reportData={data?.contactSaleSubmissionReport}
      />
    </div>
  );
}
